import {INPUT_LIMITS} from '../constants/input-limits';
import {normalizedLength} from '../text-length';
import {IFaehigkeit} from './faehigkeit.model';

export class Aufgabe {
  constructor(
    public caption: string,
    public details?: string,
    public skills?: IFaehigkeit[],
    public uuid?: string,
  ) {
  }

  public validate(): AufgabeValidation {
    return new AufgabeValidation(
      this.validateCaption(),
      this.validateDetails(),
      this.validateSkills(),
    );
  }

  /**
   * Hoechstens eine Meldung pro Feld: ein blockierendes Feld ist genau ein Fehler,
   * egal an wie vielen Grenzen es gleichzeitig scheitert.
   *
   * Die Mindestlaenge misst die um Rand-Leerzeichen gekuerzte Laenge, damit sie
   * nicht mit Leerzeichen umgangen werden kann und Frontend und Backend dieselbe
   * Regel abbilden. Ein Wert aus ausschliesslich Leerzeichen gilt als fehlend,
   * nicht als zu kurz. Die Hoechstlaenge misst die Rohlaenge, weil die Felder im
   * Formular Angulars ungetrimmtes `maxlength` tragen.
   *
   * Der Titel zaehlt darueber hinaus jede Folge von Leerraum als ein Zeichen:
   * `@Size(min = 80)` am Server misst den rohen String, so dass sich die Grenze
   * sonst mit Leerzeichen zwischen den Buchstaben umgehen laesst. Die langen
   * Freitexte bleiben bewusst bei der getrimmten Laenge — dort wuerde die
   * schaerfere Regel bereits gespeicherte Inhalte nachtraeglich ungueltig machen.
   */
  public validateCaption(): ValidationMessage[] {
    const raw = this.caption ?? '';
    const captionLength = normalizedLength(raw);

    if (captionLength === 0) {
      return [new ValidationMessage('error', 'project.validation.caption.missing')];
    }

    if (captionLength < INPUT_LIMITS.TASK_CAPTION_MIN) {
      return [new ValidationMessage('error', 'project.validation.caption.tooShort')];
    }

    if (raw.length > INPUT_LIMITS.TASK_CAPTION) {
      return [new ValidationMessage('error', 'project.validation.caption.tooLong')];
    }

    return [];
  }

  public validateDetails(): ValidationMessage[] {
    const raw = this.details ?? '';
    const detailsLength = raw.trim().length;

    if (detailsLength === 0) {
      return [new ValidationMessage('error', 'project.validation.details.missing')];
    }

    if (detailsLength < INPUT_LIMITS.TASK_DETAILS_MIN) {
      return [new ValidationMessage('error', 'project.validation.details.tooShort')];
    }

    // TASK_DETAILS ist das maxlength des Formulars und das Server-Limit — ein
    // Ueberschreiten blockiert das Speichern, ist also ein Fehler wie beim Titel
    // und kein Hinweis.
    if (raw.length > INPUT_LIMITS.TASK_DETAILS) {
      return [new ValidationMessage('error', 'project.validation.details.tooLong')];
    }

    return [];
  }

  public validateSkills(): ValidationMessage[] {
    const messages: ValidationMessage[] = [];

    if (!this.skills || this.skills.length === 0) {
      return [
        new ValidationMessage(
          'error',
          'project.validation.skills.missing',
        ),
      ];
    }

    if (this.skills.length < 3) {
      messages.push(
        new ValidationMessage(
          'info',
          'project.validation.skills.tooFew',
        ),
      );
    }

    if (this.skills.length > 10) {
      messages.push(
        new ValidationMessage(
          'info',
          'project.validation.skills.tooMany',
        ),
      );
    }

    this.skills.forEach((skill) => {
      const s = (skill.faehigkeit || '').toLowerCase().trim();
      const tokens = s
        .split('(')
        .map((x) => x.replace('(', '').replace(')', '').trim())
        .filter((x) => !!x);

      let isMatched = false;

      tokens.forEach((token) => {
        if (
          (this.caption &&
            this.caption.toLowerCase().indexOf(token) > -1) ||
          (this.details &&
            this.details.toLowerCase().indexOf(token) > -1)
        ) {
          isMatched = true;
        }
      });

      if (!isMatched) {
        messages.push(
          new ValidationMessage(
            'warn',
            'project.validation.skills.explainUsage',
            {skill: skill.faehigkeit},
          ),
        );
      }
    });

    return messages;
  }
}

export class AufgabeValidation {
  constructor(
    public caption: ValidationMessage[],
    public details: ValidationMessage[],
    public skills: ValidationMessage[],
  ) {
  }
}

export class ValidationMessage {
  constructor(
    public severity: 'error' | 'warn' | 'info',
    public key: string,
    public params?: Record<string, unknown>,
  ) {
  }
}
